// synced from loomweb src/components/.../shared/copy-button.tsx (de-Tailwind'd).
/**
 * Copy-to-clipboard button used by the ported loom block-views. Faithful to
 * loomweb's CopyButton (icon swaps to a check for ~2s after copy), with the
 * Tailwind classes replaced by namespaced `replay-copy-btn` CSS and the
 * lucide icons replaced by the local inline-SVG set. Uses the async Clipboard
 * API with a no-op fallback (the replay is read-only demo content).
 */
import type { JSX, MouseEvent } from "react";
import { useCallback, useState } from "react";
import { CopyIcon, CheckIcon } from "./icons";

interface CopyButtonProps {
  text: string;
  className?: string;
  ariaLabel?: string;
}

/** Best-effort clipboard write; silently ignores failures (read-only demo). */
async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Non-secure contexts / denied permission: no-op (demo replay is read-only).
  }
}

export function CopyButton({ text, className, ariaLabel = "复制" }: CopyButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    (e: MouseEvent): void => {
      e.stopPropagation();
      void copyToClipboard(text).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      });
    },
    [text]
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? "已复制" : ariaLabel}
      className={`replay-copy-btn${className !== undefined ? ` ${className}` : ""}`}
    >
      {copied ? (
        <CheckIcon className="replay-icon-sm replay-text-success" />
      ) : (
        <CopyIcon className="replay-icon-sm" />
      )}
    </button>
  );
}
